import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { cn } from '@/lib/utils.js';
import { getDealReopen, reopenDeal } from '@/services/deals.service.js';
import { formatHumanDate } from '@/lib/date-display.js';

// Reopen an ended deal (deals-door-1 answers round, David 2026-10-28, Q1): an EDGE-CASE tool, deliberately quiet. A client
// who comes back is a new deal, so this is never the default path: no primary action, no pipeline move, no actions sheet
// offers it. It renders only when the server says this sign-in may reopen (its own Google sign-in holding the "Reopen an
// ended deal" privilege), the deal has ended, and the word before it ended is known; the page derives none of that. The
// server is the gate (requirePersonalPrivilege); the reason is required and goes on the activity line.
//
// ENDED WHILE QUOTING (David 2026-11-04, N421SD presented on a Lost deal and the client's link was dead: "this wouldn't have to be
// a new deal since it was only ever in a quoting stage"): when the server says ended_while_quoting (the word before it ended was a
// pre-bind word and the ledger holds no bind), the key becomes a BANNER saying what the ended word does to the client's link, with
// the reopen button beside it. The deal page header and the present sheet both render it; the page derives none of it.
const REASON_MAX = 500;
export const DEAL_REOPENED_EVENT = 'tracon:deal-reopened';

// bannerOnly (the present sheet): the banner or nothing; the quiet key lives on the deal page alone.
export default function DealReopenKey({ dealId, onReopened, bannerOnly = false }) {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);

  const [reads, setReads] = useState(0);
  useEffect(() => {
    if (!dealId) return undefined;
    let alive = true;
    setState(null);
    getDealReopen(dealId).then((d) => { if (alive) setState(d || null); }).catch(() => { if (alive) setState(null); });
    return () => { alive = false; };
  }, [dealId, reads]);
  // A reopen from the other instance (the header or the present sheet) re-reads this one, so neither keeps offering it.
  useEffect(() => {
    const on = (e) => { if (String(e?.detail?.dealId || '') === String(dealId)) setReads((n) => n + 1); };
    window.addEventListener(DEAL_REOPENED_EVENT, on);
    return () => window.removeEventListener(DEAL_REOPENED_EVENT, on);
  }, [dealId]);
  useEffect(() => { if (open) { setReason(''); setChosen(''); } }, [open]);

  // QA-1 (round 2): when TRACON does not know the stage before it ended, the server lets the OWNER choose one (may_choose,
  // with the words it allows in choices). The page offers the picker only then and spells no stage word itself.
  const choosing = !!(state && state.may_choose && !(state.prior && state.prior.word) && Array.isArray(state.choices) && state.choices.length);
  const quoting = !!(state && state.ended && state.ended_while_quoting === true && state.prior && state.prior.word);
  // Ended while quoting, and this sign-in may not reopen: the banner still says what the client sees, and who can fix it.
  if (quoting && !state.may_reopen) return <EndedWhileQuotingBanner state={state} />;
  if (bannerOnly && !quoting) return null;
  if (!state || !state.ended || !state.may_reopen || (!(state.prior && state.prior.word) && !choosing)) return null;
  // A Cancelled deal is reinstated, not reopened: nothing to offer until the server says the reinstatement is on file.
  if (state.reinstatement && state.reinstatement.required && !state.reinstatement.on_file) return null;
  const prior = choosing ? chosen : state.prior.word;

  async function submitReopen() {
    const why = reason.trim();
    if (!why || (choosing && !chosen)) return;
    setBusy(true);
    try {
      const res = await reopenDeal(dealId, why, choosing ? chosen : undefined);
      if (!res || res.ok === false) throw new Error(res?.message || res?.error || 'failed');
      toast.success(`Reopened: ${res.from} to ${res.to}. The client's link works again.`);
      setOpen(false);
      setState({ ...state, ended: false, word: res.to, prior: null, may_choose: false, ended_while_quoting: false });
      try { window.dispatchEvent(new CustomEvent(DEAL_REOPENED_EVENT, { detail: { dealId: String(dealId) } })); } catch { /* the other instance re-reads on its next mount */ }
      if (typeof onReopened === 'function') onReopened(res);
    } catch (err) {
      toast.error(`Not reopened: ${err?.body?.message || err?.message || 'failed'}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {quoting ? (
        <EndedWhileQuotingBanner state={state}>
          <Button type="button" size="sm" data-deal-reopen="offered" onClick={() => setOpen(true)}>
            Reopen to {state.prior.word}
          </Button>
        </EndedWhileQuotingBanner>
      ) : (
        <Button type="button" variant="ghost" size="xs" className="text-text-dim hover:text-text" data-deal-reopen="offered"
          title="Edge case: undo an ended move made in error" onClick={() => setOpen(true)}>
          Reopen
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reopen this ended deal?</DialogTitle>
            <DialogDescription>
              {quoting ? (
                <>This puts the deal back to <strong className="text-text">{prior}</strong>, the stage it held before it was
                marked <strong className="text-text">{state.word}</strong>. It was never bound, so it reopens rather than starting a
                new deal, and the client's link works again as soon as it is reopened.</>
              ) : choosing ? (
                <>TRACON does not know the stage this deal held before it was marked <strong className="text-text">{state.word}</strong>,
                so choose the stage to put back. It is recorded as chosen by you.</>
              ) : (
                <>This puts the deal back to <strong className="text-text">{prior}</strong>, the stage it held before it was
                marked <strong className="text-text">{state.word}</strong>.</>
              )}
              {quoting ? null : (
                <>{' '}Use it only to undo an ended move made in error.
                A client who comes back is a new deal: start a renewal or a new deal from this policy instead.</>
              )}
            </DialogDescription>
          </DialogHeader>
          {choosing ? (
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Stage to put back (required)</span>
              <select
                required
                value={chosen}
                onChange={(e) => setChosen(e.target.value)}
                className="mt-1.5 flex h-9 w-full rounded-card border border-border bg-bg px-3 text-sm text-text focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">Choose a stage</option>
                {state.choices.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </label>
          ) : null}
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Reason (required)</span>
            <textarea
              autoFocus
              required
              rows={2}
              value={reason}
              maxLength={REASON_MAX}
              onChange={(e) => setReason(e.target.value)}
              placeholder={quoting ? 'e.g. client came back, re-presented' : 'e.g. marked Lost in error, the client paid'}
              className={cn(
                'mt-1.5 flex w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text',
                'placeholder:text-text-dim',
                'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-bg',
                'resize-none'
              )}
            />
          </label>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button type="button" variant="outline" onClick={submitReopen} disabled={busy || !reason.trim() || (choosing && !chosen)}>
              {busy ? 'Reopening...' : (prior ? `Reopen to ${prior}` : 'Reopen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// The banner: what the ended word does to the client's link, when and why it ended, and (children) the button that fixes it.
function EndedWhileQuotingBanner({ state, children }) {
  const p = state.prior || {};
  const day = formatHumanDate(p.at);
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card border border-warning/40 bg-warning/10 px-3 py-2" data-deal-reopen-banner="ended_while_quoting">
      <div className="min-w-0 flex-1 text-sm text-text">
        <p className="font-medium">This deal is {state.word}, so the client's link shows the ended page.</p>
        <p className="text-xs text-text-muted">
          Marked {state.word}{day ? ` on ${day}` : ''}{p.ended_reason ? ` (${p.ended_reason})` : ''} while it was {p.word}. It was never
          bound, so reopening puts it back to {p.word}.
          {children ? null : ' Your sign-in cannot reopen a deal; ask the owner.'}
        </p>
      </div>
      {children}
    </div>
  );
}
